import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { SearchIcon } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import GenericModal from 'src/components/generic-modal';
import { getUsers } from 'src/queries/query';
import CommonAlert from 'src/components/alert/CommonAlert';

export default function SendToDispatchersModal({
  open,
  handleClose,
  onSend,
  isLoading = false,
}) {
  const [loading, setLoading] = useState(false);
  const [allDispatchers, setAllDispatchers] = useState([]);
  const [filteredDispatchers, setFilteredDispatchers] = useState([]);
  const [selectedDispatchers, setSelectedDispatchers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: 'error',
  });

  const handleAlertClose = () => {
    setAlert((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const fetchDispatchers = async () => {
    try {
      setLoading(true);
      const result = await getUsers({ params: '?page=1&limit=500' });
      const dispatchers = result?.data?.filter(
        (user) => user.role?.name?.toLowerCase() === 'dispatcher'
      ) || [];
      setAllDispatchers(dispatchers);
      setFilteredDispatchers(dispatchers);
    } catch (error) {
      console.error('Error fetching dispatchers:', error?.message);
      setAlert({
        open: true,
        message: error?.message || 'Failed to fetch dispatchers',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchDispatchers();
      setSelectedDispatchers([]);
      setSearchTerm('');
    }
  }, [open]);

  useEffect(() => {
    if (allDispatchers.length > 0) {
      const filtered = allDispatchers.filter((dispatcher) => {
        const fullName = `${dispatcher.firstName || ''} ${dispatcher.lastName || ''}`.toLowerCase();
        const email = (dispatcher.email || '').toLowerCase();
        const search = searchTerm.toLowerCase();
        return fullName.includes(search) || email.includes(search);
      });
      setFilteredDispatchers(filtered);
    }
  }, [searchTerm, allDispatchers]);

  const handleToggleDispatcher = (dispatcher) => {
    setSelectedDispatchers((prev) => {
      const isSelected = prev.some((d) => d.uuid === dispatcher.uuid);
      if (isSelected) {
        return prev.filter((d) => d.uuid !== dispatcher.uuid);
      } else {
        return [...prev, dispatcher];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedDispatchers.length === filteredDispatchers.length) {
      setSelectedDispatchers([]);
    } else {
      setSelectedDispatchers([...filteredDispatchers]);
    }
  };

  const isDispatcherSelected = (dispatcherUuid) => {
    return selectedDispatchers.some((d) => d.uuid === dispatcherUuid);
  };

  const handleSend = () => {
    const dispatcherUuids = selectedDispatchers.map((d) => d.uuid);
    onSend(dispatcherUuids);
  };

  return (
    <GenericModal
      open={open}
      onClose={handleClose}
      title="Send to Dispatchers"
      primaryButtonText="Send"
      secondaryButtonText="Cancel"
      onPrimaryAction={handleSend}
      onSecondaryAction={handleClose}
      maxWidth="md"
      showCloseIcon={true}
      desktopWidth="60%"
      isLoading={isLoading}
      primaryButtonProps={{
        disabled: isLoading || selectedDispatchers.length === 0,
      }}
    >
      <Box sx={{ position: 'relative', py: 1 }}>
        <Box sx={{ opacity: isLoading || loading ? 0.5 : 1 }}>
          <Paper variant="outlined">
            <Box
              sx={{
                py: 1,
                px: 2,
                bgcolor: '#f5f5f5',
                borderBottom: '1px solid #e0e0e0',
                borderRadius: 0,
              }}
            >
              <Typography variant="subtitle2">
                Select Dispatchers ({selectedDispatchers.length} selected)
              </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                placeholder="Search by name or email"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={isLoading || loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              {loading ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 4,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={
                              filteredDispatchers.length > 0 &&
                              selectedDispatchers.length === filteredDispatchers.length
                            }
                            indeterminate={
                              selectedDispatchers.length > 0 &&
                              selectedDispatchers.length < filteredDispatchers.length
                            }
                            onChange={handleSelectAll}
                            disabled={isLoading || loading || filteredDispatchers.length === 0}
                          />
                        </TableCell>
                        <TableCell>NAME</TableCell>
                        <TableCell>EMAIL</TableCell>
                        <TableCell>PHONE</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredDispatchers.map((dispatcher) => (
                        <TableRow
                          key={dispatcher.uuid}
                          hover
                          onClick={() => handleToggleDispatcher(dispatcher)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isDispatcherSelected(dispatcher.uuid)}
                              disabled={isLoading || loading}
                            />
                          </TableCell>
                          <TableCell>
                            {dispatcher.firstName} {dispatcher.lastName}
                          </TableCell>
                          <TableCell>{dispatcher.email}</TableCell>
                          <TableCell>{dispatcher.phone || '-'}</TableCell>
                        </TableRow>
                      ))}
                      {filteredDispatchers.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            {searchTerm
                              ? 'No dispatchers found matching your search'
                              : 'No dispatchers available'}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
      <CommonAlert
        open={alert.open}
        onClose={handleAlertClose}
        message={alert.message}
        severity={alert.severity}
      />
    </GenericModal>
  );
}
